import { useMemo } from "react";
import { ASSET_TYPE_LABELS } from "domains/assets/constants/assetTypes";
import { assetTypeToAssetWithIpAdapterType } from "domains/assets/utils/assetTypeToAssetWithIpAdapterType";
import { formatFileSize, formatNumber } from "domains/commons/misc";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import {
  AsideList,
  AsideListItem,
  AsideSection,
} from "domains/ui/components/Aside";
import {
  GetAssetsByAssetIdApiResponse,
  GetJobIdApiResponse,
} from "infra/api/generated/api";
import moment from "moment";

export interface AsideSectionDetailsProps {
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  job: GetJobIdApiResponse["job"] | undefined;
}

export default function AsideSectionDetails({
  asset,
  job,
}: AsideSectionDetailsProps) {
  const { selectedTeamDetails } = useTeamContext();

  const is3d = asset?.metadata.kind === "3d";

  const author = useMemo(() => {
    if (!asset?.authorId) {
      return undefined;
    }

    const teamMember = selectedTeamDetails?.users.find(
      (user) => user.id === asset.authorId
    );
    if (teamMember) {
      return teamMember.email;
    }

    const teamApiKey = selectedTeamDetails?.keys.find(
      (key) => key.id === asset.authorId
    );
    if (teamApiKey) {
      return `API Key - ${teamApiKey.name}`;
    }

    return asset.authorId;
  }, [asset?.authorId, selectedTeamDetails?.keys, selectedTeamDetails?.users]);

  return (
    <AsideSection id="details" title="Details">
      <AsideList>
        <AsideListItem
          label="Type"
          value={
            asset
              ? ASSET_TYPE_LABELS[
                  assetTypeToAssetWithIpAdapterType({
                    assetType: asset.metadata.type,
                    ipAdapterType: job?.metadata?.input?.ipAdapterType,
                  })
                ]
              : "-"
          }
        />

        {asset?.metadata.type !== "texture" && !is3d && (
          <AsideListItem
            label="Dimensions"
            value={
              asset?.metadata.width && asset?.metadata.height
                ? `${asset.metadata.width} x ${asset.metadata.height} px`
                : "-"
            }
          />
        )}

        {asset?.metadata.kind === "video" && (
          <AsideListItem
            label="Duration"
            value={
              asset?.properties.duration
                ? `${asset.properties.duration.toFixed(2)}s`
                : "-"
            }
          />
        )}

        {asset?.metadata.type === "vectorization" && (
          <AsideListItem
            label="Size"
            value={
              asset?.metadata.size &&
              formatFileSize(asset.metadata.size, true, 1)
            }
          />
        )}

        {is3d && (
          <>
            <AsideListItem
              label="Vertices"
              value={
                asset?.properties.vertexCount !== undefined
                  ? formatNumber(asset?.properties.vertexCount)
                  : "-"
              }
            />
            <AsideListItem
              label="Triangles"
              value={
                asset?.properties.faceCount !== undefined
                  ? formatNumber(asset?.properties.faceCount)
                  : "-"
              }
            />
          </>
        )}

        <AsideListItem
          label="Created"
          value={moment(asset?.createdAt).format("MMM DD, YYYY, LT")}
        />

        {asset?.privacy === "private" && (
          <AsideListItem label="Author" value={author} copyable isTruncated />
        )}

        {asset?.metadata.seed && (
          <AsideListItem label="Seed" value={asset?.metadata.seed} copyable />
        )}

        <AsideListItem
          label="Asset ID"
          value={asset?.id}
          copyable
          isTruncated
        />
      </AsideList>
    </AsideSection>
  );
}
